import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'vopay';
import { BodyText } from 'UI';
import Webhooks from 'shared/webhooks';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import { usePartnerData } from '@/providers/PartnerData.provider';
import AccountScopedApiProvider from './AccountScopedApiProvider';
import useAccountWebhooksHostProps from './useAccountWebhooksHostProps';

/**
 * The account page's Webhooks tab (ID-13743): the client portal's shared Webhooks, Manage and History,
 * scoped to this account by its own bearer. A view-only role can look but not save, test or retrigger.
 */
export default function WebhooksTab() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { accountId } = useParams<{ accountId: string }>();
  const { bearer, hasSettled } = useAccountBearer(accountId);
  const hostProps = useAccountWebhooksHostProps(accountId);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  // Both sub-tabs fetch on mount; they must not fire against the partner session before the token is in.
  if (!bearer && !hasSettled) return <Skeleton height={320} data-testid="webhooksTabLoading" />;

  // The lookup has answered and there is no token to scope the feature with — see TransactionsTab.
  if (!bearer) {
    return (
      <BodyText data-testid="webhooksTabUnavailable">
        {t('unavailable', { defaultValue: "This account's data could not be loaded." })}
      </BodyText>
    );
  }

  return (
    <AccountScopedApiProvider bearer={bearer} readOnly={isRoleViewOnly}>
      <Webhooks {...hostProps} readOnly={isRoleViewOnly} />
    </AccountScopedApiProvider>
  );
}
